import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Undo,
  Redo,
  Code,
  FormatQuote,
  Highlight as HighlightIcon,
} from "@mui/icons-material";

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const handleFormat = (event, newFormats) => {
    // Ce handler ne fait rien car chaque bouton a son propre onClick
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        padding: 2,
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "#f5f5f5",
        alignItems: "center",
      }}
    >
      {/* Undo / Redo */}
      <ToggleButtonGroup size="small" aria-label="undo redo">
        <Tooltip title="Annuler">
          <ToggleButton
            value="undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Refaire">
          <ToggleButton
            value="redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Taille de texte */}
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <Select
          value={editor.getAttributes("textStyle").fontSize || "16px"}
          onChange={(e) =>
            editor
              .chain()
              .focus()
              .setMark("textStyle", { fontSize: e.target.value })
              .run()
          }
          displayEmpty
        >
          <MenuItem value="12px">Petit</MenuItem>
          <MenuItem value="16px">Normal</MenuItem>
          <MenuItem value="20px">Grand</MenuItem>
          <MenuItem value="24px">Très grand</MenuItem>
          <MenuItem value="32px">Titre</MenuItem>
        </Select>
      </FormControl>

      {/* Couleur de texte */}
      <FormControl size="small">
        <input
          type="color"
          value={editor.getAttributes("textStyle").color || "#000000"}
          onChange={(e) =>
            editor.chain().focus().setColor(e.target.value).run()
          }
          style={{
            height: "36px",
            width: "50px",
            cursor: "pointer",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
          title="Couleur du texte"
        />
      </FormControl>

      <Divider orientation="vertical" flexItem />

      {/* Format de texte */}
      <ToggleButtonGroup size="small" aria-label="text formatting">
        <Tooltip title="Gras">
          <ToggleButton
            value="bold"
            selected={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBold />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Italique">
          <ToggleButton
            value="italic"
            selected={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalic />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Souligné">
          <ToggleButton
            value="underline"
            selected={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlined />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Surligner">
          <ToggleButton
            value="highlight"
            selected={editor.isActive("highlight")}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
          >
            <HighlightIcon />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Code">
          <ToggleButton
            value="code"
            selected={editor.isActive("code")}
            onClick={() => editor.chain().focus().toggleCode().run()}
          >
            <Code />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Listes */}
      <ToggleButtonGroup size="small" aria-label="lists">
        <Tooltip title="Liste à puces">
          <ToggleButton
            value="bulletList"
            selected={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulleted />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Liste numérotée">
          <ToggleButton
            value="orderedList"
            selected={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FormatListNumbered />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Citation">
          <ToggleButton
            value="blockquote"
            selected={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <FormatQuote />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      {/* Alignement */}
      <ToggleButtonGroup size="small" aria-label="text alignment">
        <Tooltip title="Aligner à gauche">
          <ToggleButton
            value="left"
            selected={editor.isActive({ textAlign: "left" })}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <FormatAlignLeft />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Centrer">
          <ToggleButton
            value="center"
            selected={editor.isActive({ textAlign: "center" })}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <FormatAlignCenter />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Aligner à droite">
          <ToggleButton
            value="right"
            selected={editor.isActive({ textAlign: "right" })}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <FormatAlignRight />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Justifier">
          <ToggleButton
            value="justify"
            selected={editor.isActive({ textAlign: "justify" })}
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          >
            <FormatAlignJustify />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Box>
  );
};

const RichTextEditor = ({
  content,
  onChange,
  disabled = false,
  placeholder = "Commencez à écrire...",
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Highlight.configure({
        multicolor: true,
      }),
      Color,
      TextStyle,
      FontFamily,
    ],
    content: content || "",
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  // Mettre à jour le contenu de l'éditeur quand la prop content change
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content, editor]);

  // Mettre à jour l'état editable
  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  return (
    <Paper elevation={2} sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
      <MenuBar editor={editor} />
      <Box
        sx={{
          minHeight: 300,
          maxHeight: 500,
          overflowY: "auto",
          padding: 2,
          "& .ProseMirror": {
            outline: "none",
            minHeight: 250,
            "& p.is-editor-empty:first-of-type::before": {
              content: `"${placeholder}"`,
              color: "#adb5bd",
              float: "left",
              height: 0,
              pointerEvents: "none",
            },
            "& ul, & ol": {
              paddingLeft: "1.5rem",
            },
            "& h1, & h2, & h3": {
              marginTop: "1rem",
              marginBottom: "0.5rem",
              fontWeight: "bold",
            },
            "& blockquote": {
              borderLeft: "3px solid #ccc",
              paddingLeft: "1rem",
              marginLeft: 0,
              fontStyle: "italic",
            },
            "& code": {
              backgroundColor: "#f4f4f4",
              padding: "0.2rem 0.4rem",
              borderRadius: "3px",
              fontFamily: "monospace",
            },
            "& mark": {
              backgroundColor: "#ffeb3b",
              padding: "0.1rem 0.2rem",
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

export default RichTextEditor;
